(() => {
  if (customElements.get('hx-announcement-marquee')) return;
  customElements.define('hx-announcement-marquee', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.rows = [...this.querySelectorAll('[data-news-index]')];
      this.index = Math.max(0, this.rows.findIndex(row => row.classList.contains('is-active')));
      this.toggle = this.querySelector('.hx-news-toggle');
      this.window = this.querySelector('.hx-news-window');
      this.tracks = this.rows.map(row => {
        const track = row.querySelector('.hx-news-track');
        const groups = [...track.querySelectorAll('.hx-news-group')];
        return { track, groups, seed: groups[0].firstElementChild.cloneNode(true) };
      });
      this.motion = matchMedia('(prefers-reduced-motion:reduce)');
      this.toggleAttribute('data-paused', this.motion.matches);
      this.toggle.addEventListener('click', () => { this.toggleAttribute('data-paused'); this.schedule(); }, options);
      this.updateVisibility = () => { this.toggleAttribute('data-suspended', document.hidden); this.schedule(); };
      document.addEventListener('visibilitychange', this.updateVisibility, options);
      this.motion.addEventListener('change', () => { this.toggleAttribute('data-paused', this.motion.matches); this.schedule(); }, options);
      this.queueLayout = () => {
        if (this.layoutFrame) return;
        this.layoutFrame = requestAnimationFrame(() => { this.layoutFrame = 0; this.fitTracks(); });
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.queueLayout);
        this.resizeObserver.observe(this.window);
      } else window.addEventListener('resize', this.queueLayout, options);
      document.fonts?.addEventListener('loadingdone', this.queueLayout, options);
      const controller = this.controller;
      document.fonts?.ready.then(() => { if (this.controller === controller) this.queueLayout(); });
      this.fitTracks();
      this.setAttribute('data-ready', '');
      this.updateVisibility();
    }
    fitTracks() {
      if (!this.isConnected || !this.window.clientWidth) return;
      const viewport = this.window.clientWidth;
      const changes = this.tracks.flatMap(state => {
        const width = state.groups[0].firstElementChild.getBoundingClientRect().width;
        if (!width) return [];
        const count = Math.max(2, Math.ceil(viewport / width) + 1);
        if (state.groups.every(group => group.children.length === count)) return [];
        const transform = getComputedStyle(state.track).transform;
        const values = transform.slice(transform.indexOf('(') + 1, -1).split(',').map(Number);
        const distance = transform === 'none' ? 0 : -(values[transform.startsWith('matrix3d') ? 12 : 4] || 0);
        return [{ state, count, distance, duration: 170 * count / 16 }];
      });
      changes.forEach(({ state, count, duration }) => {
        state.groups.forEach(group => {
          while (group.children.length > count) group.lastElementChild.remove();
          if (group.children.length < count) {
            const fragment = document.createDocumentFragment();
            for (let index = group.children.length; index < count; index++) {
              const item = state.seed.cloneNode(true);
              item.setAttribute('aria-hidden', 'true');
              fragment.append(item);
            }
            group.append(fragment);
          }
        });
        state.track.style.setProperty('--hx-news-duration', `${duration}s`);
      });
      const phases = changes.map(change => ({ ...change, width: change.state.groups[0].getBoundingClientRect().width }));
      phases.forEach(({ state, distance, duration, width }) => {
        const animation = state.track.getAnimations?.().find(item => item.animationName === 'hxNewsMarquee');
        if (animation && width) animation.currentTime = ((distance % width + width) % width) / width * duration * 1000;
      });
    }
    schedule() {
      clearTimeout(this.timer);
      const paused = this.hasAttribute('data-paused');
      this.toggle.setAttribute('aria-pressed', String(paused));
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.resume : this.toggle.dataset.pause);
      if (paused || this.hasAttribute('data-suspended') || !this.rows.length) return;
      this.timer = setTimeout(() => {
        this.index = (this.index + 1) % this.rows.length;
        this.rows.forEach((row, index) => {
          row.classList.toggle('is-active', index === this.index);
          row.setAttribute('aria-hidden', String(index !== this.index));
          row.inert = index !== this.index;
        });
        this.schedule();
      }, 3000);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = 0;
    }
  });
})();
